import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { 
  History, 
  Search, 
  RotateCcw, 
  CheckCircle, 
  AlertTriangle, 
  Calendar,
  User,
  FileText,
  Users,
  Clock,
  Eye,
  ArrowRight,
  Undo2
} from 'lucide-react';

/**
 * BulkOperationsHistory - Audit trail and rollback interface for bulk operations
 * 
 * Features:
 * - Chronological operation history
 * - Operation details and impact view
 * - Rollback capability with impact analysis  
 * - Search and filter operations
 * - Admin audit trail
 * - Operation comparison
 */
const BulkOperationsHistory = ({ 
  onRollback,
  onViewDetails,
  className = ""
}) => {
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOperations, setFilteredOperations] = useState([]);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [rollbackAnalysis, setRollbackAnalysis] = useState(null);

  // Load operations history
  useEffect(() => {
    const loadOperationsHistory = async () => {
      try {
        setLoading(true);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1200));
        
        // Mock operations history data
        const mockOperations = [
          {
            id: 'bulk-op-001',
            operationType: 'Bulk Upload',
            status: 'completed',
            startedAt: '2024-12-12T14:30:00Z',
            completedAt: '2024-12-12T14:32:15Z',
            performedBy: 'admin@avenue.ca',
            affectedSessions: ['scb-s1', 'scb-s2', 'hbw-s1'],
            affectedPrograms: ['Self-Care for Burnout', 'Healthy Boundaries at Work'],
            filesProcessed: 8,
            versionsCreated: 3,
            sosAffected: 12,
            totalSizeBytes: 15728640, // 15MB
            changeNotes: 'Updated handouts with new branding and added video resources',
            versionIncrementType: 'minor',
            canRollback: true,
            rollbackReason: null
          },
          {
            id: 'bulk-op-002',
            operationType: 'Bulk Update',
            status: 'completed',
            startedAt: '2024-12-11T16:45:00Z',
            completedAt: '2024-12-11T16:47:30Z',
            performedBy: 'admin@avenue.ca',
            affectedSessions: ['le-s1', 'le-s2', 'le-s3'],
            affectedPrograms: ['Leadership Essentials'],
            filesProcessed: 12,
            versionsCreated: 3,
            sosAffected: 7,
            totalSizeBytes: 25165824, // 24MB
            changeNotes: 'Major content refresh for Q1 2025 program updates',
            versionIncrementType: 'major',
            canRollback: true,
            rollbackReason: null
          },
          {
            id: 'bulk-op-003',
            operationType: 'Bulk Delete',
            status: 'completed',
            startedAt: '2024-12-10T10:15:00Z',
            completedAt: '2024-12-10T10:16:45Z',
            performedBy: 'admin@avenue.ca',
            affectedSessions: ['cs-s1', 'cs-s2'],
            affectedPrograms: ['Communication Skills'],
            filesProcessed: 0,
            versionsCreated: 2,
            sosAffected: 8,
            totalSizeBytes: 0,
            changeNotes: 'Removed outdated materials from Communication Skills sessions',
            versionIncrementType: 'major',
            canRollback: true,
            rollbackReason: null
          },
          {
            id: 'bulk-op-004',
            operationType: 'Rollback Operation',
            status: 'completed',
            startedAt: '2024-12-09T09:30:00Z',
            completedAt: '2024-12-09T09:31:20Z',
            performedBy: 'admin@avenue.ca',
            affectedSessions: ['hbw-s3'],
            affectedPrograms: ['Healthy Boundaries at Work'],
            filesProcessed: 5,
            versionsCreated: 0,
            sosAffected: 2,
            totalSizeBytes: 8388608, // 8MB
            changeNotes: 'Rolled back to v1.2.0 due to content errors in v1.3.0',
            versionIncrementType: null,
            canRollback: false,
            rollbackReason: 'Content errors detected'
          },
          {
            id: 'bulk-op-005',
            operationType: 'Bulk Upload',
            status: 'failed',
            startedAt: '2024-12-08T13:20:00Z',
            completedAt: '2024-12-08T13:22:10Z',
            performedBy: 'admin@avenue.ca',
            affectedSessions: ['scb-s4'],
            affectedPrograms: ['Self-Care for Burnout'],
            filesProcessed: 0,
            versionsCreated: 0,
            sosAffected: 0,
            totalSizeBytes: 0,
            changeNotes: 'Attempted to upload new video content',
            versionIncrementType: 'minor',
            canRollback: false,
            rollbackReason: null,
            errorMessage: 'File validation failed: Video format not supported'
          }
        ];

        setOperations(mockOperations);
        setFilteredOperations(mockOperations);
        
      } catch (err) {
        console.error('Failed to load operations history:', err);
        setError('Failed to load operations history');
      } finally {
        setLoading(false);
      }
    };

    loadOperationsHistory();
  }, []);

  // Filter operations based on search
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = operations.filter(op => 
        op.operationType.toLowerCase().includes(query) ||
        op.changeNotes.toLowerCase().includes(query) ||
        op.performedBy.toLowerCase().includes(query) ||
        op.affectedPrograms.some(program => 
          program.toLowerCase().includes(query)
        )
      );
      setFilteredOperations(filtered);
    } else {
      setFilteredOperations(operations);
    }
  }, [searchQuery, operations]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end - start;
    const seconds = Math.round(durationMs / 1000);
    
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getOperationIcon = (operationType, status) => {
    if (status === 'failed') {
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    }
    
    switch (operationType) {
      case 'Bulk Upload':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'Bulk Update':
        return <Calendar className="h-4 w-4 text-green-500" />;
      case 'Bulk Delete':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'Rollback Operation':
        return <RotateCcw className="h-4 w-4 text-purple-500" />;
      default:
        return <CheckCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-green-100 text-green-800">Completed</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'in_progress':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">In Progress</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleRollbackClick = async (operation) => {
    setSelectedOperation(operation);
    
    // Simulate rollback impact analysis
    const mockAnalysis = {
      canRollback: operation.canRollback,
      affectedSessions: operation.affectedSessions,
      affectedSOs: operation.sosAffected,
      potentialIssues: operation.canRollback ? [] : [
        { type: 'warning', message: 'This operation cannot be rolled back' }
      ],
      estimatedDuration: '30-60 seconds',
      backupRequired: true
    };
    
    setRollbackAnalysis(mockAnalysis);
  };

  const confirmRollback = () => {
    if (onRollback && selectedOperation) {
      onRollback({
        operationId: selectedOperation.id,
        analysis: rollbackAnalysis
      });
    }
    setSelectedOperation(null);
    setRollbackAnalysis(null);
  };

  const handleViewDetails = (operation) => {
    if (onViewDetails) {
      onViewDetails(operation);
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-80" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center space-x-3 p-4 border rounded-lg">
                <Skeleton className="h-8 w-8" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-64" />
                  <Skeleton className="h-4 w-96" />
                </div>
                <Skeleton className="h-8 w-20" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-600 font-medium">Failed to load operations history</p>
          <p className="text-gray-500 text-sm mt-1">{error}</p>
          <Button 
            onClick={() => window.location.reload()} 
            variant="outline" 
            className="mt-4"
          >
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <History className="h-5 w-5" />
          <span>Operations History</span>
        </CardTitle>
        <CardDescription>
          View past bulk operations and manage rollbacks
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search operations, programs, or admin..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Operations List */}
        <div className="space-y-3">
          {filteredOperations.length === 0 ? (
            <div className="text-center py-8">
              <History className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No operations found</p>
            </div>
          ) : (
            filteredOperations.map((operation) => (
              <div
                key={operation.id}
                className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                  operation.status === 'failed' 
                    ? 'border-red-200 bg-red-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    {getOperationIcon(operation.operationType, operation.status)}
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900">{operation.operationType}</h3>
                        {getStatusBadge(operation.status)}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                        <span className="flex items-center space-x-1">
                          <User className="h-3 w-3" />
                          <span>{operation.performedBy}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Clock className="h-3 w-3" />
                          <span>{formatDate(operation.startedAt)}</span>
                        </span>
                        {operation.completedAt && (
                          <span>Duration: {getDuration(operation.startedAt, operation.completedAt)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Operation Details */}
                <div className="mb-3">
                  <p className="text-sm text-gray-700 mb-2">{operation.changeNotes}</p>
                  
                  <div className="flex items-center space-x-6 text-sm text-gray-600">
                    <span className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3" />
                      <span>{operation.affectedSessions.length} sessions</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Users className="h-3 w-3" />
                      <span>{operation.sosAffected} SOs</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <FileText className="h-3 w-3" />
                      <span>{operation.filesProcessed} files</span>
                    </span>
                    {operation.totalSizeBytes > 0 && (
                      <span>{formatFileSize(operation.totalSizeBytes)}</span>
                    )}
                  </div>

                  {/* Affected Programs */}
                  <div className="flex items-center space-x-2 mt-2">
                    <span className="text-xs text-gray-500">Programs:</span>
                    {operation.affectedPrograms.map((program, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {program}
                      </Badge>
                    ))}
                  </div>

                  {/* Error Message */}
                  {operation.status === 'failed' && operation.errorMessage && (
                    <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-sm text-red-700">
                      <strong>Error:</strong> {operation.errorMessage}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(operation)}
                      className="flex items-center space-x-1"
                    >
                      <Eye className="h-3 w-3" />
                      <span>View Details</span>
                    </Button>
                    
                    {operation.canRollback && operation.status === 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRollbackClick(operation)}
                        className="flex items-center space-x-1 text-orange-600 border-orange-300 hover:bg-orange-50"
                      >
                        <Undo2 className="h-3 w-3" />
                        <span>Rollback</span>
                      </Button>
                    )}
                  </div>

                  {operation.versionIncrementType && (
                    <Badge variant="outline" className="text-xs">
                      {operation.versionIncrementType} increment
                    </Badge>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Rollback Confirmation Modal */}
        {selectedOperation && rollbackAnalysis && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Undo2 className="h-5 w-5 text-orange-500" />
                  <span>Confirm Rollback</span>
                </CardTitle>
                <CardDescription>
                  This will revert the changes from the selected operation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm font-medium text-orange-800 mb-1">
                    Operation: {selectedOperation.operationType}
                  </p>
                  <p className="text-sm text-orange-700">
                    {selectedOperation.changeNotes}
                  </p>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Affected Sessions:</span>
                    <span className="font-medium">{rollbackAnalysis.affectedSessions.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Affected SOs:</span>
                    <span className="font-medium">{rollbackAnalysis.affectedSOs}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estimated Duration:</span>
                    <span className="font-medium">{rollbackAnalysis.estimatedDuration}</span>
                  </div>
                </div>

                {rollbackAnalysis.potentialIssues.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-red-800">Potential Issues:</p>
                    {rollbackAnalysis.potentialIssues.map((issue, index) => (
                      <p key={index} className="text-sm text-red-600">• {issue.message}</p>
                    ))}
                  </div>
                )}

                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedOperation(null);
                      setRollbackAnalysis(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmRollback}
                    disabled={!rollbackAnalysis.canRollback}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Confirm Rollback
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BulkOperationsHistory;